<script lang="ts">
	import {
		resetSceneMainFilter,
		roomsScenesFilter,
		sceneMainFilter,
		getSelectedSceneFilterCount,
	} from 'src/stores/ui/filters';
	import { defaultSelectedRoom } from 'src/helpers/constants';
	import { ComponentType } from '@Shelly/shelly-design-system/helpers/enums';
	import { edit } from 'src/stores/ui/edit_lists';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { is_mobile, is_tablet } from 'src/stores';

	import ScenesFilterModal from './ScenesFilterModal.svelte';

	/**
	 * Scenes header. New layout: renders a Toolbar with search/filter/chips and
	 * publishes a kebab (Reorder items, optional AI Scene Creator / Script Scene
	 * entries) into `$listHeader` for the tabs action slot. Room-scoped path
	 * uses the shared `RoomToolbar` chain. Old layout preserved verbatim.
	 */
	import { onDestroy } from 'svelte';
	import { format } from 'svelte-i18n';
	import {
		Toolbar,
		Breadcrumbs,
		IconButton,
		Input,
		Chip,
		Button,
		BreadcrumbsFlexWrapper,
	} from '@Shelly/shelly-design-system';
	import RoomToolbar from '../Rooms/RoomToolbar.svelte';
	import RoomBreadCrumbs from '../Rooms/RoomBreadCrumbs.svelte';
	import { resetDrawer } from 'src/helpers/drawer_helpers';
	import { isExperimentalEnabled } from 'src/helpers/user_settings_helpers';
	import { createListHeaderHandle, type ListHeaderConfig } from 'src/stores/list_header';
	import { capFirstChar } from 'src/helpers/utilities';

	export let type = 'scenes';
	export let id: string | undefined;
	export let lazyShowChatModal: () => void;
	export let showAddScriptSceneModal: boolean;
	export let disableReorder = false;

	const headerHandle = createListHeaderHandle();

	let openFilterModal = false;

	$: filterCount = getSelectedSceneFilterCount($roomsScenesFilter);
	$: notification =
		filterCount > 0 ? ({ title: filterCount, type: ComponentType.DANGER } as const) : null;

	function resetData() {
		resetSceneMainFilter();
		roomsScenesFilter.set({ rooms: [defaultSelectedRoom] });
	}

	$: if ($showNewLayout && type === 'scenes') {
		const hideInlineSearch = $is_mobile || $is_tablet;
		if ($edit) {
			headerHandle.set({
				primaryAction: {
					label: $format('label_finish_reordering', { default: 'Finish reordering' }),
					icon: 'fas fa-xmark',
					variant: 'secondary',
					onClick: () => edit.set(false),
				},
			});
		} else {
			const items = [
				{
					title: $format('label_reorder_items', { default: 'Reorder items' }),
					icon: 'fa-solid fa-arrows-up-down-left-right',
					disabled: disableReorder,
					onClick: () => {
						resetDrawer();
						edit.set(true);
					},
				},
			];

			if (isExperimentalEnabled('inf1')) {
				items.push({
					title: $format('label_open_scene_ai_assistant', {
						default: 'Open Scene Creator Assistant',
					}),
					icon: 'fa-solid fa-microchip-ai',
					disabled: false,
					onClick: () => lazyShowChatModal(),
				});
			}

			if (isExperimentalEnabled('inf2')) {
				items.push({
					title: $format('label_add_script_scene', { default: 'Add Script Scene' }),
					icon: 'fa-brands fa-js',
					disabled: false,
					onClick: () => {
						showAddScriptSceneModal = !showAddScriptSceneModal;
					},
				});
			}

			const config: ListHeaderConfig = {
				menuItems: [{ items }],
				search: hideInlineSearch
					? {
							value: $sceneMainFilter,
							label: capFirstChar($format('search', { default: 'Search' })),
							id: 'scenes-kebab-search-input',
							onInput: (value) => sceneMainFilter.set(value),
							filter: {
								onClick: () => {
									openFilterModal = true;
								},
								ariaLabel: $format('aria_open_filter', { default: 'Open filter' }),
								notification,
							},
						}
					: null,
			};
			headerHandle.set(config);
		}
	}

	onDestroy(() => {
		if (type === 'scenes') {
			headerHandle.clear();
		}
	});
</script>

{#if $showNewLayout && type === 'scenes'}
	{#if !$edit}
		{@const hideInlineSearch = $is_mobile || $is_tablet}
		{#if !hideInlineSearch || notification}
			<Toolbar
				class="mt-m mb-m"
				isSticky
				useNewLayout
				noBreadcrumbs
				hideSlotLeftSide={hideInlineSearch}
				hideSlotRightSide
				hideSlotBottom={!notification}
			>
				<svelte:fragment slot="leftSide">
					{#if !hideInlineSearch}
						<div class="d-flex align-items-center justify-content-center">
							<Input
								class="width-md-l mr-sm-s text-capitalize-placeholder"
								bind:value={$sceneMainFilter}
								type="text"
								label={capFirstChar($format('search', { default: 'Search' }))}
								icon="fas fa-search"
								id="scenes-toolbar-search-input"
								aria-labelledby="scenes-toolbar-search-input"
							/>
							<IconButton
								icon="fa-solid fa-filter"
								on:click={() => {
									openFilterModal = true;
								}}
								aria-label={$format('aria_open_filter', { default: 'Open filter' })}
								{notification}
							/>
						</div>
					{/if}
				</svelte:fragment>

				<svelte:fragment slot="bottom">
					{#if notification}
						<div class="d-flex align-items-center">
							{#each $roomsScenesFilter.rooms as room}
								<Chip
									aria-label={room.title}
									showChip
									title={room.title}
									class="mr-s flex-shrink-0"
									on:close={resetData}
								/>
							{/each}
							<IconButton
								on:click={resetData}
								icon="fas fa-xmark"
								variant="secondary"
								class="width-full"
							>
								{$format('button_filter_modal_clear_all', { default: 'Clear all' })}
							</IconButton>
						</div>
					{/if}
				</svelte:fragment>
			</Toolbar>
		{/if}
	{/if}
{:else}
	<Toolbar
		class="mb-m"
		isSticky
		useNewLayout={$showNewLayout}
		hideSlotLeftSide={$edit || (!$is_mobile && type !== 'room')}
		hideSlotRightSide={($is_mobile && type !== 'room') || !notification}
		hideSlotBottom={!notification || $edit || ($is_mobile && type !== 'room')}
	>
		<svelte:fragment slot="breadcrumbs">
			{#if type === 'room'}
				<RoomBreadCrumbs {id} />
			{:else if $is_mobile && type !== 'room'}
				<BreadcrumbsFlexWrapper gap="s">
					<div slot="left" class="flex-1 min-width-0 max-width-fit-content">
						<Breadcrumbs
							listClasses="width-full"
							links={[{ label: capFirstChar($format('tab_scenes')) }]}
							aria-label={$format('label_scenes', { default: 'Scenes' })}
						/>
					</div>

					<div slot="right">
						{#if isExperimentalEnabled('inf1')}
							<IconButton
								aria-label="Open Scene Creator Assistant"
								class="align-self-center"
								icon="fa-solid fa-microchip-ai"
								variant="secondary"
								disabled={false}
								on:click={lazyShowChatModal}
							/>
						{/if}

						{#if isExperimentalEnabled('inf2') && !$edit}
							<IconButton
								aria-label="Add Script Scene"
								class="align-self-center"
								icon="fa-brands fa-js"
								variant="secondary"
								disabled={false}
								on:click={() => {
									showAddScriptSceneModal = !showAddScriptSceneModal;
								}}
							/>
						{/if}

						{#if notification}
							<Button
								size="m"
								variant="secondary"
								class="align-self-center"
								disabled={disableReorder}
								icon={$edit ? '' : 'fa-solid fa-arrows-up-down-left-right'}
								on:click={() => {
									resetDrawer();
									edit.set(!$edit);
								}}
							>
								{$edit
									? $format('button_finish_reordering_label', {
											default: 'Finish reordering',
										})
									: $format('reorder_items_button_label', {
											default: 'Reorder items',
										})}
							</Button>
						{/if}
					</div>
				</BreadcrumbsFlexWrapper>
			{:else if !$is_mobile && type !== 'room'}
				<BreadcrumbsFlexWrapper gap="s">
					<div
						class="d-flex align-items-center flex-1 min-width-0 max-width-fit-content gap-s"
						slot="left"
					>
						<Breadcrumbs
							class="flex-1 min-width-0"
							listClasses="width-full"
							links={[{ label: capFirstChar($format('tab_scenes')) }]}
							aria-label={$format('label_scenes', { default: 'Scenes' })}
						/>

						{#if !$edit}
							<div class="d-flex align-items-center justify-content-center">
								<Input
									class="width-md-l mr-sm-s text-capitalize-placeholder"
									bind:value={$sceneMainFilter}
									type="text"
									label={capFirstChar($format('search', { default: 'Search' }))}
									icon="fas fa-search"
									id="scenes-toolbar-search-input"
									aria-labelledby="scenes-toolbar-search-input"
								/>
								<IconButton
									icon="fa-solid fa-filter"
									on:click={() => {
										openFilterModal = true;
									}}
									aria-label={$format('aria_open_filter', { default: 'Open filter' })}
									{notification}
								/>
							</div>
						{/if}
					</div>

					<div slot="right">
						{#if isExperimentalEnabled('inf1')}
							<IconButton
								aria-label="Open Scene Creator Assistant"
								class="align-self-center"
								icon="fa-solid fa-microchip-ai"
								variant="secondary"
								disabled={false}
								on:click={lazyShowChatModal}
							/>
						{/if}

						{#if isExperimentalEnabled('inf2') && !$edit}
							<IconButton
								aria-label="Add Script Scene"
								class="align-self-center"
								icon="fa-brands fa-js"
								variant="secondary"
								disabled={false}
								on:click={() => {
									showAddScriptSceneModal = !showAddScriptSceneModal;
								}}
							/>
						{/if}

						{#if notification}
							<Button
								size="m"
								variant="secondary"
								class="align-self-center"
								disabled={disableReorder}
								icon={$edit ? '' : 'fa-solid fa-arrows-up-down-left-right'}
								on:click={() => {
									resetDrawer();
									edit.set(!$edit);
								}}
							>
								{$edit
									? $format('button_finish_reordering_label', {
											default: 'Finish reordering',
										})
									: $format('reorder_items_button_label', {
											default: 'Reorder items',
										})}
							</Button>
						{/if}
					</div>
				</BreadcrumbsFlexWrapper>
			{/if}
		</svelte:fragment>

		<svelte:fragment slot="leftSide">
			{#if type === 'room'}
				<RoomToolbar />
			{/if}

			{#if type === 'scenes'}
				<div class="d-flex align-items-center justify-content-center">
					<Input
						class="width-md-l mr-sm-s text-capitalize-placeholder"
						bind:value={$sceneMainFilter}
						type="text"
						label={capFirstChar($format('search', { default: 'Search' }))}
						icon="fas fa-search"
						id="scenes-toolbar-search-input"
						aria-labelledby="scenes-toolbar-search-input"
					/>
					<IconButton
						icon="fa-solid fa-filter"
						on:click={() => {
							openFilterModal = true;
						}}
						aria-label={$format('aria_open_filter', { default: 'Open filter' })}
						{notification}
					/>
				</div>

				{#if notification && $is_mobile}
					<div class="d-flex align-items-center">
						<IconButton
							on:click={resetData}
							icon="fas fa-xmark"
							variant="secondary"
							class="width-full"
						>
							{$format('button_filter_modal_clear_all', { default: 'Clear all' })}
						</IconButton>
					</div>
				{/if}
			{/if}
		</svelte:fragment>

		<svelte:fragment slot="bottom">
			{#if notification}
				<div class="d-flex align-items-center">
					{#each $roomsScenesFilter.rooms as room}
						<Chip
							aria-label={room.title}
							showChip
							title={room.title}
							class="mr-s flex-shrink-0"
							on:close={resetData}
						/>
					{/each}
					<IconButton
						on:click={resetData}
						icon="fas fa-xmark"
						variant="secondary"
						class="width-full"
					>
						{$format('button_filter_modal_clear_all', { default: 'Clear all' })}
					</IconButton>
				</div>
			{/if}
		</svelte:fragment>
	</Toolbar>
{/if}

{#if openFilterModal}
	<ScenesFilterModal bind:openFilterModal />
{/if}
